import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, InputNumber } from 'antd';
import { appMessage as message } from '../../utils/appMessage';
import type { ColumnsType } from 'antd/es/table';
import { CloudUploadOutlined } from '@ant-design/icons';

import {
  FONT_SIZE, FONT_WEIGHT, LINE_HEIGHT, PAGE_SIZE_OPTIONS, RADIUS, SHADOW, SPACING,
} from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { formatPrice } from '../../utils/format';
import { fetchExpansionRegisterPreview } from '../../api/api';
import {
  ExpansionOption, RegistrableGameType, RegistrableSearchResult,
  REGISTER_PLACEHOLDER_PRICE, REGISTER_INIT_STOCK,
  registrationCode as codeOf,
} from '../../types/productRegistration';
import type { FilterOption } from '../../hooks/UseCardView';
import { AppButton } from '../common/AppButton';
import { AppTag } from '../common/AppTag';
import { textColumn } from '../common/TableHelpers';
import { RegisterEmptyState } from '../registerEmptyState/RegisterEmptyState';
import { ProductRegisterTable } from '../productRegisterTable/ProductRegisterTable';
import { ExpansionFilterSelect } from '../common/ExpansionFilterSelect';

/** 미리보기 표 기본 페이지 크기 — 확장팩엔 수백 장이 있을 수 있어(M2A 484장) 전부
 *  한 번에 그리면(특히 재고 입력이 행마다 붙은 뒤로) 느려서, 페이지네이션으로 나눠
 *  보여준다(행 자체는 전부 볼 수 있다 — 페이지 이동 또는 페이지 크기 변경). */
const PREVIEW_PAGE_SIZE = 50;

export interface ExpansionRegisterSummary {
  expansionCode:  string;
  expansionLabel: string;
  newCount:       number;
  dupCount:       number;
  /** 코드별 초기 재고 — 미리보기 표에서 직원이 입력한 값. 표에 안 뜬 나머지 카드
   *  (PREVIEW_ROW_LIMIT 초과분)은 여기 없고 백엔드가 1로 채운다. */
  stockByCode:    Record<string, number>;
  /** 신규 생성될 전체 카드의 재고 합계(표에 안 뜬 카드는 1로 계산). */
  totalStock:     number;
}

interface Props {
  gameType:          RegistrableGameType;
  expansions:        ExpansionOption[];
  loadingExpansions: boolean;
  /** 게임 전환 시 페이지가 null로 리셋한다. */
  selectedCode:      string | null;
  onSelectCode:      (code: string | null) => void;
  /** 다른 등록 잡이 진행 중이면 true — 등록 버튼을 잠근다(중복 등록 방지). */
  busy?:             boolean;
  /** "신규 N건 등록" 클릭 → 페이지가 확인 모달을 연다. */
  onRegisterClick:   (summary: ExpansionRegisterSummary) => void;
}

// ─── 3-카운트 요약 카드 ───────────────────────────────────────────────────────

const CountCard: React.FC<{
  label:      string;
  count:      number;
  unit:       string;
  topBorder:  string;
  countColor: string;
  desc:       string;
  /** 첫 카드가 아니면 좌측에 세로 구분선(디자인의 1px 해어라인). */
  divider:    boolean;
}> = ({ label, count, unit, topBorder, countColor, desc, divider }) => {
  const t = useThemeTokens();
  return (
    <div style={{
      background: t.bgSurface, padding: `${SPACING.sm}px ${SPACING.md}px`,
      borderTop: `3px solid ${topBorder}`,
      borderLeft: divider ? `1px solid ${t.border}` : undefined,
    }}>
      <div style={{ fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold, color: countColor }}>{label}</div>
      <div style={{ fontSize: FONT_SIZE.xl, fontWeight: FONT_WEIGHT.bold, lineHeight: LINE_HEIGHT.tight, marginTop: SPACING.xxs, color: countColor }}>
        {count.toLocaleString()}
        <span style={{ fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.semibold, marginLeft: SPACING.xxs }}>{unit}</span>
      </div>
      <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, marginTop: SPACING.xxs, lineHeight: LINE_HEIGHT.normal }}>
        {desc}
      </div>
    </div>
  );
};

// ─── ExpansionRegisterPanel ──────────────────────────────────────────────────

/**
 * "확장팩 일괄 등록" 탭(2026-08-31, "상품 등록 개선안"). 확장팩을 고르면 그 확장팩에서
 * 새로 생성될 상품 수·상품명을 먼저 보여주고("생성될 상품 미리보기"), 확인 모달을 거친
 * 뒤에만 실제 등록한다 — 되돌리기 어려운 쓰기 작업이라 실행 전 확인이 필요하다.
 */
export const ExpansionRegisterPanel: React.FC<Props> = ({
  gameType, expansions, loadingExpansions, selectedCode, onSelectCode, busy = false, onRegisterClick,
}) => {
  const t = useThemeTokens();

  const [rows, setRows]             = useState<RegistrableSearchResult[]>([]);
  const [loadedCode, setLoadedCode] = useState<string | null>(null);
  const [loading, setLoading]       = useState(false);
  const [showDup, setShowDup]       = useState(false);
  /** 코드별로 직원이 고친 초기 재고. 안 고친 코드는 기본값(REGISTER_INIT_STOCK)으로 취급. */
  const [stockByCode, setStockByCode] = useState<Record<string, number>>({});
  /** "재고 일괄 → 전체 적용"용 입력값. 미리보기 표에 안 뜨는 카드(PREVIEW_ROW_LIMIT 초과분)의
   *  재고는 이 방법으로만 바꿀 수 있다. */
  const [bulkStock, setBulkStock] = useState(REGISTER_INIT_STOCK);

  const expansion = useMemo(
    () => expansions.find((e) => e.code === selectedCode) ?? null,
    [expansions, selectedCode],
  );

  const filterOptions = useMemo<FilterOption[]>(() => expansions.map((e) => ({
    value: e.code,
    label: e.nameKr || e.name,
    count: e.cardCount,
    releaseDate: e.releaseDate,
    productType: e.productType,
    registeredCount: e.registeredCount,
  })), [expansions]);

  useEffect(() => {
    setStockByCode({});
    setBulkStock(REGISTER_INIT_STOCK);
    if (!selectedCode) { setRows([]); setLoadedCode(null); return; }
    let cancelled = false;
    setLoading(true);
    setShowDup(false);
    fetchExpansionRegisterPreview(gameType, selectedCode)
      .then((r) => { if (!cancelled) { setRows(r); setLoadedCode(selectedCode); } })
      .catch(() => { if (!cancelled) { setRows([]); setLoadedCode(selectedCode); message.error('미리보기를 불러오지 못했습니다'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [gameType, selectedCode]);

  // 선택 직후 이전 확장팩 결과가 잠깐 보이지 않도록, 현재 선택과 로드된 코드가 다르면 로딩으로 취급.
  const pending = loading || (selectedCode != null && loadedCode !== selectedCode);

  const newRows = useMemo(() => rows.filter((r) => !r.alreadyRegistered), [rows]);
  const dupRows = useMemo(() => rows.filter((r) => r.alreadyRegistered), [rows]);
  const newCount = newRows.length;
  const dupCount = dupRows.length;

  const visibleRows = showDup ? rows : newRows;

  const totalStock = useMemo(
    () => newRows.reduce((sum, r) => sum + (stockByCode[codeOf(r)] ?? REGISTER_INIT_STOCK), 0),
    [newRows, stockByCode],
  );

  // 표에 안 뜨는 카드까지 포함해 신규 전체에 같은 재고를 박는다(개별 입력이 이후 덮어씀).
  const applyBulkStock = () =>
    setStockByCode(Object.fromEntries(newRows.map((r) => [codeOf(r), bulkStock])));

  const columns = useMemo<ColumnsType<RegistrableSearchResult>>(() => [
    textColumn<RegistrableSearchResult>(t, '등록될 상품명', 'previewName', { ellipsis: true }),
    {
      title: '카드번호', key: 'cardNumber', width: 130, ellipsis: true,
      render: (_, r) => <span style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary }}>{r.candidate.cardNumber}</span>,
    },
    {
      title: '재고', key: 'stock', width: 92, align: 'right',
      render: (_, r) => {
        if (r.alreadyRegistered) return <span style={{ color: t.textMuted }}>—</span>;
        const code = codeOf(r);
        return (
          <InputNumber
            min={1}
            size="small"
            value={stockByCode[code] ?? REGISTER_INIT_STOCK}
            onChange={(v) => setStockByCode((prev) => ({ ...prev, [code]: v ?? 1 }))}
            style={{ width: 68 }}
          />
        );
      },
    },
    {
      title: '상태', key: 'status', width: 130,
      render: (_, r) => <AppTag variant="registerStatus" created={!r.alreadyRegistered} />,
    },
  ], [t, stockByCode]);

  const panelBox: React.CSSProperties = {
    background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: RADIUS.lg, boxShadow: SHADOW.card,
  };

  const expansionLabel = expansion ? `${expansion.code} · ${expansion.nameKr || expansion.name}` : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.md }}>
      {/* 확장팩 선택 */}
      <div style={{ ...panelBox, padding: `${SPACING.md}px ${SPACING.lg}px`, display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap' }}>
        <ExpansionFilterSelect
          value={selectedCode ?? 'ALL'}
          onChange={(v) => onSelectCode(v === 'ALL' ? null : v)}
          options={filterOptions}
          loading={loadingExpansions}
          gameType={gameType}
          allowAll={false}
          placeholder="등록할 확장팩 선택"
          style={{ width: 320 }}
        />
        {selectedCode
          ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap' }}>
              <AppButton
                variant="primary"
                icon={<CloudUploadOutlined />}
                loading={pending}
                disabled={busy || (!pending && newCount === 0)}
                disabledReason={busy ? '다른 상품 등록 작업이 진행 중입니다' : '이 확장팩은 전부 등록되어 있습니다'}
                onClick={() => onRegisterClick({
                  expansionCode: selectedCode, expansionLabel, newCount, dupCount,
                  // 직원이 실제로 고친 코드만 넘긴다 — 나머지는 백엔드가 기본 재고(1)로 채운다.
                  stockByCode, totalStock,
                })}
              >
                신규 {newCount.toLocaleString()}건 등록
              </AppButton>
              {dupCount > 0 && (
                <span style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary }}>
                  이미 등록된 {dupCount.toLocaleString()}건은 건너뜁니다
                </span>
              )}
            </div>
          )
          : (
            <span style={{ fontSize: FONT_SIZE.sm, color: t.textMuted }}>
              확장팩을 선택하면 등록될 상품을 미리 확인할 수 있습니다
            </span>
          )}
      </div>

      {/* 빈 상태 */}
      {!selectedCode && (
        <div style={{ ...panelBox }}>
          <RegisterEmptyState
            title="등록할 확장팩을 선택하세요"
            description={<>
              선택하면 <b>새로 생성될 상품 수</b>와 상품명을 먼저 보여줍니다.<br />
              확인 후 등록하므로 잘못된 확장팩을 실수로 올릴 일이 없습니다.
            </>}
          />
        </div>
      )}

      {/* 3-카운트 요약 + 미리보기 표 */}
      {selectedCode && (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            border: `1px solid ${t.border}`, borderRadius: RADIUS.lg, overflow: 'hidden',
          }}>
            <CountCard
              divider={false}
              label="새로 생성" count={newCount} unit="건"
              topBorder={t.accent} countColor={t.accent}
              desc={`판매중지 · 가격 ${formatPrice(REGISTER_PLACEHOLDER_PRICE)} · 재고 기본 ${REGISTER_INIT_STOCK}장(수정 가능)`}
            />
            <CountCard
              divider
              label="이미 등록됨 — 건너뜀" count={dupCount} unit="건"
              topBorder={t.border} countColor={t.textSecondary}
              desc="기존 상품의 가격·재고는 건드리지 않습니다"
            />
            <CountCard
              divider
              label="확장팩 전체" count={expansion?.cardCount ?? 0} unit="장"
              topBorder={t.borderSubtle} countColor={t.textMuted}
              desc={expansion ? `${expansion.nameKr || expansion.name}${expansion.releaseDate ? ` · 발매 ${expansion.releaseDate}` : ''}` : ''}
            />
          </div>

          <div style={{ ...panelBox, overflow: 'hidden' }}>
            <div style={{
              padding: `${SPACING.sm}px ${SPACING.lg}px`, borderBottom: `1px solid ${t.border}`,
              display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>
                생성될 상품 미리보기
              </span>
              <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>
                {showDup ? `신규 ${newCount}건 + 건너뛸 ${dupCount}건` : `신규 생성되는 ${newCount}건`}
              </span>

              {newCount > 0 && (
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: SPACING.xs }}>
                  <span style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>재고 일괄</span>
                  <InputNumber
                    min={1}
                    size="small"
                    value={bulkStock}
                    onChange={(v) => setBulkStock(v ?? 1)}
                    style={{ width: 64 }}
                  />
                  <AppButton variant="default" size="sm" onClick={applyBulkStock}>전체 적용</AppButton>
                </div>
              )}
              {dupCount > 0 && (
                <Checkbox
                  style={{ marginLeft: newCount > 0 ? undefined : 'auto', fontSize: FONT_SIZE.sm }}
                  checked={showDup}
                  onChange={(e) => setShowDup(e.target.checked)}
                >
                  이미 등록된 {dupCount}건도 표시
                </Checkbox>
              )}
            </div>

            <ProductRegisterTable
              rows={visibleRows}
              columns={columns}
              loading={pending}
              pagination={{
                pageSize: PREVIEW_PAGE_SIZE,
                showSizeChanger: true,
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                hideOnSinglePage: true,
                size: 'small',
              }}
              rowClassName={(r) => (r.alreadyRegistered ? 'register-preview-dup-row' : '')}
              locale={{ emptyText: '표시할 상품이 없습니다' }}
            />

            <div style={{
              padding: `${SPACING.xs}px ${SPACING.lg}px`, background: t.bgRaised, borderTop: `1px solid ${t.border}`,
              fontSize: FONT_SIZE.xs, color: t.textSecondary,
            }}>
              등록 시 이 목록의 신규 상품이 모두 생성됩니다
            </div>
          </div>
        </>
      )}
    </div>
  );
};
